import { Fragment, useMemo, useState } from "react";
import { format } from "date-fns";
import { MessageSquare, Plane } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PersonHoverCard } from "@/components/people/PersonHoverCard";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  ProjectAvailability,
  ProjectFunctionGroup,
  ProjectFunctionSeries,
  ProjectPersonTravelDay,
} from "@/hooks/useProjectPeople";
import { cn } from "@/lib/utils";
import {
  getMainFunctionGroupForPerson,
  sortByMainFunctionGroup,
} from "@/lib/people-planning-groups";
import {
  getPersonInitials,
  useHorizontalScrollState,
} from "./pinnedColumnUtils";

interface FunctionEntry {
  name: string;
  remark: string | null;
  assignmentId: string;
  groupId: string | null;
  groupName: string | null;
}

interface PeopleOverviewTableProps {
  availability: ProjectAvailability[];
  functions: ProjectFunctionSeries[];
  groups: ProjectFunctionGroup[];
  travelDays: ProjectPersonTravelDay[];
  eventDays: Date[];
  projectDays: Date[];
  cameraNumberByAssignmentId?: Map<string, string> | Record<string, string>;
  onUpdateUnityUsername: (
    availabilityId: string,
    unityUsername: string,
  ) => void;
}

const UNITY_USERNAME_PATTERN = /^[A-Za-z0-9._-]+$/;

function dayToString(day: Date) {
  return format(day, "yyyy-MM-dd");
}

function getCameraNumber(
  map: Map<string, string> | Record<string, string> | undefined,
  assignmentId: string,
) {
  if (!map) return undefined;
  if (map instanceof Map) return map.get(assignmentId);
  return map[assignmentId];
}

export function PeopleOverviewTable({
  availability,
  functions,
  groups,
  travelDays,
  eventDays,
  projectDays,
  cameraNumberByAssignmentId,
  onUpdateUnityUsername,
}: PeopleOverviewTableProps) {
  const { scrollRef, isScrolledX, handleScroll } = useHorizontalScrollState();
  const [editingAvailabilityId, setEditingAvailabilityId] = useState<
    string | null
  >(null);
  const [unityUsernameDraft, setUnityUsernameDraft] = useState("");

  const groupById = useMemo(
    () => new Map(groups.map((group) => [group.id, group] as const)),
    [groups],
  );
  const eventDayValues = useMemo(
    () => eventDays.map(dayToString).sort(),
    [eventDays],
  );

  const bookedPeople = useMemo(
    () =>
      sortByMainFunctionGroup(
        availability
          .filter((a) => a.status === "booked")
          .map((person) => ({
            availability: person,
            primaryGroup: getMainFunctionGroupForPerson({
              personId: person.person_id,
              functions,
              groupsById: groupById,
              eventDays: eventDayValues,
            }),
            name: `${person.person?.first_name ?? ""} ${person.person?.last_name ?? ""}`,
          })),
      ),
    [availability, eventDayValues, functions, groupById],
  );

  // Build a lookup: personId -> date -> FunctionEntry[]
  const assignmentsByPersonAndDay = useMemo(() => {
    const map: Record<string, Record<string, FunctionEntry[]>> = {};
    for (const series of functions) {
      for (const day of series.days) {
        for (const assignment of day.assignments ?? []) {
          if (!map[assignment.person_id]) {
            map[assignment.person_id] = {};
          }
          if (!map[assignment.person_id][day.function_date]) {
            map[assignment.person_id][day.function_date] = [];
          }
          map[assignment.person_id][day.function_date].push({
            name: series.name,
            remark: assignment.remark,
            assignmentId: assignment.id,
            groupId: series.project_function_group_id ?? null,
            groupName: series.project_function_group_id
              ? (groupById.get(series.project_function_group_id)?.name ?? null)
              : null,
          });
        }
      }
    }
    return map;
  }, [functions, groupById]);

  const travelDayByPersonAndDate = useMemo(() => {
    const map = new Map<string, ProjectPersonTravelDay>();
    for (const travelDay of travelDays) {
      map.set(`${travelDay.person_id}:${travelDay.travel_date}`, travelDay);
    }
    return map;
  }, [travelDays]);

  if (bookedPeople.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        No booked people yet.
      </div>
    );
  }

  const saveUnityUsername = (
    availabilityId: string,
    draftValue: string,
    currentValue: string,
    personUnityUsername: string,
  ) => {
    const trimmedValue = draftValue.trim();

    if (trimmedValue === currentValue.trim()) {
      setEditingAvailabilityId(null);
      return;
    }

    if (trimmedValue.length > 100) {
      toast.error("Unity username must be 100 characters or fewer.");
      return;
    }

    if (trimmedValue && !UNITY_USERNAME_PATTERN.test(trimmedValue)) {
      toast.error(
        "Unity username can only contain letters, numbers, periods, underscores, and hyphens.",
      );
      return;
    }

    const normalizedValue =
      !trimmedValue || trimmedValue === personUnityUsername ? "" : trimmedValue;

    onUpdateUnityUsername(availabilityId, normalizedValue);
    setEditingAvailabilityId(null);
  };

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="relative w-full min-w-0 max-w-full overflow-x-auto"
    >
      <table className="min-w-max caption-bottom text-sm">
        <TableHeader>
          <TableRow>
            <TableHead
              className={cn(
                "sticky left-0 z-10 bg-background transition-[width,min-width] duration-150",
                isScrolledX
                  ? "w-14 min-w-14 max-w-14 text-center"
                  : "min-w-[200px]",
              )}
              title="Person"
            >
              {isScrolledX ? "P" : "Person"}
            </TableHead>
            <TableHead className="min-w-[120px]">Phone</TableHead>
            <TableHead>Unity</TableHead>
            {projectDays.map((day) => (
              <TableHead
                key={day.toISOString()}
                className="min-w-[140px] text-center"
              >
                <div className="text-xs">
                  <div>{format(day, "EEE")}</div>
                  <div className="font-normal">{format(day, "dd.MM")}</div>
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookedPeople.map(({ availability: person, primaryGroup }, index) => {
            const personFunctions =
              assignmentsByPersonAndDay[person.person_id] ?? {};
            const personUnityUsername =
              person.person?.unity_username?.trim() ?? "";
            const effectiveUnityUsername =
              person.unity_username ?? person.person?.unity_username ?? "";
            const personName = person.person
              ? `${person.person.first_name} ${person.person.last_name}`
              : "Unknown";
            const previousGroup = bookedPeople[index - 1]?.primaryGroup;
            const showGroupHeader =
              index === 0 || previousGroup?.id !== primaryGroup?.id;

            return (
              <Fragment key={person.id}>
                {showGroupHeader && (
                  <TableRow>
                    <TableCell
                      colSpan={projectDays.length + 3}
                      className="sticky left-0 z-10 bg-muted/80 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {primaryGroup?.name ?? "Ungrouped"}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    className={cn(
                      "sticky left-0 z-10 bg-background font-medium transition-[width,min-width] duration-150",
                      isScrolledX
                        ? "w-14 min-w-14 max-w-14 text-center"
                        : "min-w-[200px]",
                    )}
                  >
                    {person.person ? (
                      <PersonHoverCard
                        personId={person.person.id}
                        firstName={person.person.first_name}
                        lastName={person.person.last_name}
                        personType={person.person.person_type}
                        email={person.person.email}
                        phone={person.person.phone}
                      >
                        <span
                          className={cn(
                            isScrolledX
                              ? "inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold"
                              : "",
                          )}
                          title={personName}
                        >
                          {isScrolledX
                            ? getPersonInitials(
                                person.person.first_name,
                                person.person.last_name,
                              )
                            : `${person.person.first_name} ${person.person.last_name}`}
                        </span>
                      </PersonHoverCard>
                    ) : (
                      "Unknown"
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {person.person?.phone || "—"}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {editingAvailabilityId === person.id ? (
                      <Input
                        autoFocus
                        className="h-8 w-[120px]"
                        aria-label={`Unity username for ${personName}`}
                        value={unityUsernameDraft}
                        onChange={(e) => setUnityUsernameDraft(e.target.value)}
                        onBlur={() =>
                          saveUnityUsername(
                            person.id,
                            unityUsernameDraft,
                            effectiveUnityUsername,
                            personUnityUsername,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            saveUnityUsername(
                              person.id,
                              unityUsernameDraft,
                              effectiveUnityUsername,
                              personUnityUsername,
                            );
                          }

                          if (e.key === "Escape") {
                            setEditingAvailabilityId(null);
                            setUnityUsernameDraft(effectiveUnityUsername);
                          }
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="min-h-8 rounded px-1 py-1 text-left text-sm text-foreground"
                        aria-label={`Edit Unity username for ${personName}`}
                        onDoubleClick={() => {
                          setEditingAvailabilityId(person.id);
                          setUnityUsernameDraft(effectiveUnityUsername);
                        }}
                      >
                        {effectiveUnityUsername || "—"}
                      </button>
                    )}
                  </TableCell>
                  {projectDays.map((day) => {
                    const dayStr = dayToString(day);
                    const entries = personFunctions[dayStr] ?? [];
                    const travelDay = travelDayByPersonAndDate.get(
                      `${person.person_id}:${dayStr}`,
                    );
                    const entriesByGroup = new Map<string, FunctionEntry[]>();
                    for (const entry of entries) {
                      const key = entry.groupId ?? "__ungrouped__";
                      entriesByGroup.set(key, [
                        ...(entriesByGroup.get(key) ?? []),
                        entry,
                      ]);
                    }
                    return (
                      <TableCell
                        key={day.toISOString()}
                        className="p-2 text-center align-top"
                      >
                        {entries.length > 0 || travelDay ? (
                          <div className="space-y-0.5">
                            {travelDay && (
                              <div className="flex items-center justify-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                                <Plane className="h-3 w-3 shrink-0" />
                                <span>Travel</span>
                              </div>
                            )}
                            {[...entriesByGroup.entries()].map(
                              ([groupId, groupEntries]) => (
                                <div key={groupId} className="space-y-0.5">
                                  {groupId !== "__ungrouped__" && (
                                    <div className="text-[10px] font-medium text-muted-foreground">
                                      {groupEntries[0]?.groupName}
                                    </div>
                                  )}
                                  {groupEntries.map((entry, i) => {
                                    const cameraNumber = getCameraNumber(
                                      cameraNumberByAssignmentId,
                                      entry.assignmentId,
                                    );
                                    return (
                                      <div
                                        key={i}
                                        className="flex items-center justify-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs"
                                      >
                                        <span className="truncate">
                                          {entry.name}
                                        </span>
                                        {cameraNumber && (
                                          <Badge
                                            variant="outline"
                                            className="h-4 px-1 text-[10px] shrink-0"
                                          >
                                            CAM{cameraNumber}
                                          </Badge>
                                        )}
                                        {entry.remark && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <MessageSquare className="h-3 w-3 shrink-0 text-muted-foreground" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              {entry.remark}
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ),
                            )}
                          </div>
                        ) : null}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </table>
    </div>
  );
}
